import { FC, useState } from "react";
import {
  Dialog,
  FormControlLabel,
  IconButton,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
  Tooltip,
  Divider,
  CircularProgress,
} from "@mui/material";
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { MobilePaperComponent, PaperComponent } from "../../../Paper/CustomPaper";
import User from "../../../../Models/UserModels/User";
import { UpdateUserSettings } from "../../../../Services/LondunarkerfiAPIService";

interface UserSettingsModalProps {
  open: boolean;
  toggleOpen: () => void;
  user: User;
}

const UserSettingsModal: FC<UserSettingsModalProps> = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState<{ [key: string]: boolean }>({
    wantsToBeginWithVehicle: false,
    wantsToPinVehicle: false,
    defaultWeightNoteTypeAsReweighting: false,
    sendCopyToSender: false,
    wantsToPinVehicleBrutto: false,
    wantsToKeepWeightModalOpen: false,
  });

  const handleClose = () => props.toggleOpen();

  const handleToggle = async (key: keyof User["settings"]) => {
    setLoading((prev) => ({ ...prev, [key]: true }));
    const updatedSettings = {
      ...props.user.settings,
      [key]: !props.user.settings[key],
    };

    try {
      await UpdateUserSettings(updatedSettings);
      props.user.settings[key] = updatedSettings[key];
    } catch (error) {
      console.error("Failed to update user settings:", error);
    } finally {
      setLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      PaperComponent={isMobile || isTablet ? MobilePaperComponent : PaperComponent}
      aria-labelledby="draggable-dialog-title"
      maxWidth={'sm'}
    >
      <Box id="draggable-dialog-title"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'move',
        }}
      >
        <Typography variant="h5" component="h2" fontWeight="bold">
          Notendastillingar
        </Typography>
        <IconButton onClick={handleClose} size="large">
          <CancelIcon />
        </IconButton>
      </Box>
      <Divider />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={props.user.settings.wantsToBeginWithVehicle}
                onChange={() => handleToggle("wantsToBeginWithVehicle")}
                color="primary"
                disabled={loading.wantsToBeginWithVehicle}
              />
            }
            label="Byrja skráningu á flutningstæki"
          />
          {loading.wantsToBeginWithVehicle && <CircularProgress size={20} />}
          <Tooltip title="Stillir kerfið þannig að aflaskráning byrjar á skráningu flutningstækis í stað veiðistofns.">
            <HelpOutlineIcon color="action" sx={{ fontSize: 20 }} />
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={props.user.settings.wantsToPinVehicle || props.user.settings.wantsToPinVehicleBrutto}
                onChange={async () => {
                  // If turning ON, default to wantsToPinVehicle
                  if (!(props.user.settings.wantsToPinVehicle || props.user.settings.wantsToPinVehicleBrutto)) {
                    setLoading((prev) => ({ ...prev, wantsToPinVehicle: true, wantsToPinVehicleBrutto: false }));
                    try {
                      await UpdateUserSettings({
                        ...props.user.settings,
                        wantsToPinVehicle: true,
                        wantsToPinVehicleBrutto: false,
                      });
                      props.user.settings.wantsToPinVehicle = true;
                      props.user.settings.wantsToPinVehicleBrutto = false;
                    } catch (error) {
                      console.error("Failed to update user settings:", error);
                    } finally {
                      setLoading((prev) => ({ ...prev, wantsToPinVehicle: false, wantsToPinVehicleBrutto: false }));
                    }
                  } else {
                    // If turning OFF, clear both
                    setLoading((prev) => ({ ...prev, wantsToPinVehicle: true, wantsToPinVehicleBrutto: true }));
                    try {
                      await UpdateUserSettings({
                        ...props.user.settings,
                        wantsToPinVehicle: false,
                        wantsToPinVehicleBrutto: false,
                      });
                      props.user.settings.wantsToPinVehicle = false;
                      props.user.settings.wantsToPinVehicleBrutto = false;
                    } catch (error) {
                      console.error("Failed to update user settings:", error);
                    } finally {
                      setLoading((prev) => ({ ...prev, wantsToPinVehicle: false, wantsToPinVehicleBrutto: false }));
                    }
                  }
                }}
                color="primary"
                disabled={loading.wantsToPinVehicle || loading.wantsToPinVehicleBrutto}
              />
            }
            label="Halda flutningstæki föstu á milli vigtana"
          />
          {(loading.wantsToPinVehicle || loading.wantsToPinVehicleBrutto) && <CircularProgress size={20} />}
          <Tooltip title="Ef flutningstæki er skráð á vigtanótu mun það halda sér fyrir aðrar aflaskráningar undir sömu nótu. Ef þú vilt frekar halda síðasta brúttó, veldu það hér fyrir neðan.">
            <HelpOutlineIcon color="action" sx={{ fontSize: 20 }} />
          </Tooltip>
        </Box>
        {(props.user.settings.wantsToPinVehicle || props.user.settings.wantsToPinVehicleBrutto) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 4 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={props.user.settings.wantsToPinVehicle}
                  onChange={async () => {
                    if (!props.user.settings.wantsToPinVehicle) {
                      setLoading((prev) => ({ ...prev, wantsToPinVehicle: true, wantsToPinVehicleBrutto: true }));
                      try {
                        await UpdateUserSettings({
                          ...props.user.settings,
                          wantsToPinVehicle: true,
                          wantsToPinVehicleBrutto: false,
                        });
                        props.user.settings.wantsToPinVehicle = true;
                        props.user.settings.wantsToPinVehicleBrutto = false;
                      } catch (error) {
                        console.error("Failed to update user settings:", error);
                      } finally {
                        setLoading((prev) => ({ ...prev, wantsToPinVehicle: false, wantsToPinVehicleBrutto: false }));
                      }
                    }
                  }}
                  color="primary"
                  disabled={loading.wantsToPinVehicle || loading.wantsToPinVehicleBrutto || props.user.settings.wantsToPinVehicle}
                />
              }
              label="Törun"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={props.user.settings.wantsToPinVehicleBrutto}
                  onChange={async () => {
                    if (!props.user.settings.wantsToPinVehicleBrutto) {
                      setLoading((prev) => ({ ...prev, wantsToPinVehicle: true, wantsToPinVehicleBrutto: true }));
                      try {
                        await UpdateUserSettings({
                          ...props.user.settings,
                          wantsToPinVehicle: false,
                          wantsToPinVehicleBrutto: true,
                        });
                        props.user.settings.wantsToPinVehicle = false;
                        props.user.settings.wantsToPinVehicleBrutto = true;
                      } catch (error) {
                        console.error("Failed to update user settings:", error);
                      } finally {
                        setLoading((prev) => ({ ...prev, wantsToPinVehicle: false, wantsToPinVehicleBrutto: false }));
                      }
                    }
                  }}
                  color="primary"
                  disabled={loading.wantsToPinVehicle || loading.wantsToPinVehicleBrutto || props.user.settings.wantsToPinVehicleBrutto}
                />
              }
              label="Síðasta brúttó"
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={props.user.settings.defaultWeightNoteTypeAsReweighting}
                onChange={() => handleToggle("defaultWeightNoteTypeAsReweighting")}
                color="primary"
                disabled={loading.defaultWeightNoteTypeAsReweighting}
              />
            }
            label="Sjálfkrafa velja til endurvigtunar ef vigtunarleyfi finnst"
          />
          {loading.defaultWeightNoteTypeAsReweighting && <CircularProgress size={20} />}
          <Tooltip title="Ef þessi stilling er virk mun kerfið sjálfkrafa velja til endurvigtunar ef vigtunarleyfi finnst þegar ný vigtanóta er búin til.">
            <HelpOutlineIcon color="action" sx={{ fontSize: 20 }} />
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={props.user.settings.sendCopyToSender}
                onChange={() => handleToggle("sendCopyToSender")}
                color="primary"
                disabled={loading.sendCopyToSender}
              />
            }
            label="Senda afrit á sendanda"
          />
          {loading.sendCopyToSender && <CircularProgress size={20} />}
          <Tooltip title="Ef þessi stilling er virk mun kerfið velja að senda afrit á sendanda hakið sé á. Hægt að breyta því þegar hlutir eru sendir.">
            <HelpOutlineIcon color="action" sx={{ fontSize: 20 }} />
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={props.user.settings.wantsToKeepWeightModalOpen}
                onChange={() => handleToggle("wantsToKeepWeightModalOpen")}
                color="primary"
                disabled={loading.wantsToKeepWeightModalOpen}
              />
            }
            label="Halda aflaskráningu opni á milli vigtana"
          />
          {loading.wantsToKeepWeightModalOpen && <CircularProgress size={20} />}
          <Tooltip title="Ef þessi stilling er virk mun kerfið halda aflaskráningu opni á milli vigtana. Þegar þú skráir aflann, geturu þá strax skráð næsta eftir að hafa smellt á Enter.">
            <HelpOutlineIcon color="action" sx={{ fontSize: 20 }} />
          </Tooltip>
        </Box>
      </Box>
    </Dialog>
  );
};

export default UserSettingsModal;